/**
 * Plugin Widget Registry
 *
 * Manages the **catalog** of reusable widget *definitions* (types) that plugins
 * contribute. Each definition describes a widget's metadata, size constraints,
 * config schema, and rendering component. The widget registry does NOT manage
 * placement, layout, or persistence — those are handled by the Dashboard Registry.
 *
 * Relationship with Dashboard Registry (`dashboard-registry.ts`):
 * - **Widget Registry** = "what widgets exist" (type catalog / component library)
 * - **Dashboard Registry** = "where widgets are placed" (layout, panels, persistence)
 *
 * Plugins register widget definitions here via `registerWidget()`. The dashboard
 * then instantiates and positions widgets using the Dashboard Registry.
 *
 * Works in conjunction with backend PluginWidgetRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';
import React from 'react';

/**
 * Widget size configuration
 */
export interface WidgetSize {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
  defaultWidth: number;
  defaultHeight: number;
}

/**
 * Widget configuration field
 */
export interface WidgetConfigField {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'color' | 'entity-picker';
  label: string;
  description?: string;
  default?: any;
  required?: boolean;
  options?: Array<{ label: string; value: any }>;
  entityType?: string;
}

/**
 * Widget definition
 */
export interface WidgetDefinition {
  /** Unique widget type ID */
  type: string;
  /** Plugin that owns this widget */
  pluginId: string;
  /** Human-readable name */
  name: string;
  /** Description */
  description?: string;
  /** Icon */
  icon?: string;
  /** Category for grouping */
  category: string;
  /** Size configuration */
  size: WidgetSize;
  /** React component to render */
  component?: React.ComponentType<WidgetProps>;
  /** Component URL for iframe */
  componentUrl?: string;
  /** Configuration fields */
  configFields?: WidgetConfigField[];
  /** Whether widget is enabled */
  enabled: boolean;
}

/**
 * Widget instance
 */
export interface WidgetInstance {
  /** Unique instance ID */
  id: string;
  /** Widget type */
  type: string;
  /** Plugin ID */
  pluginId: string;
  /** Position on grid */
  position: { x: number; y: number };
  /** Size on grid */
  size: { width: number; height: number };
  /** Instance configuration */
  config: Record<string, any>;
}

/**
 * Props passed to widget components
 */
export interface WidgetProps {
  instance: WidgetInstance;
  config: Record<string, any>;
  onConfigChange?: (config: Record<string, any>) => void;
  isEditing?: boolean;
}

/**
 * Widget registry state
 */
interface WidgetRegistryState {
  /** Map of widget type to definition */
  widgets: Map<string, WidgetDefinition>;
  /** Map of category to widget types */
  widgetsByCategory: Map<string, Set<string>>;
  /** Map of instance ID to instance */
  instances: Map<string, WidgetInstance>;
  /** Currently editing instance ID */
  editingInstanceId: string | null;
  
  // Actions
  registerWidget: (widget: WidgetDefinition) => void;
  unregisterWidget: (widgetType: string) => void;
  unregisterPluginWidgets: (pluginId: string) => void;
  addInstance: (instance: WidgetInstance) => void;
  updateInstance: (instanceId: string, updates: Partial<WidgetInstance>) => void;
  removeInstance: (instanceId: string) => void;
  setEditingInstance: (instanceId: string | null) => void;
  getWidget: (widgetType: string) => WidgetDefinition | undefined;
  getAllWidgets: () => WidgetDefinition[];
  getWidgetsByCategory: () => Map<string, WidgetDefinition[]>;
  getInstance: (instanceId: string) => WidgetInstance | undefined;
  getAllInstances: () => WidgetInstance[];
  syncFromBackend: (widgets: WidgetDefinition[], instances: WidgetInstance[]) => void;
}

/**
 * Widget registry store
 */
export const useWidgetRegistry = create<WidgetRegistryState>((set, get) => ({
  widgets: new Map(),
  widgetsByCategory: new Map(),
  instances: new Map(),
  editingInstanceId: null,

  registerWidget: (widget: WidgetDefinition) => {
    // P2-7: Warn when widget has no renderable component
    if (!widget.component && !widget.componentUrl) {
      console.warn(
        `[WidgetRegistry] Widget "${widget.type}" from plugin "${widget.pluginId}" registered without a component or componentUrl — it will not be renderable.`,
      );
    }

    set((state) => {
      const newWidgets = new Map(state.widgets);
      const newByCategory = new Map(state.widgetsByCategory);
      
      // Remove from old category if updating
      const existing = newWidgets.get(widget.type);
      if (existing) {
        newByCategory.get(existing.category)?.delete(widget.type);
      }
      
      newWidgets.set(widget.type, widget);
      
      if (!newByCategory.has(widget.category)) {
        newByCategory.set(widget.category, new Set());
      }
      newByCategory.get(widget.category)!.add(widget.type);
      
      return { widgets: newWidgets, widgetsByCategory: newByCategory };
    });
  },

  unregisterWidget: (widgetType: string) => {
    set((state) => {
      const widget = state.widgets.get(widgetType);
      if (!widget) return state;
      
      const newWidgets = new Map(state.widgets);
      const newByCategory = new Map(state.widgetsByCategory);
      const newInstances = new Map(state.instances);
      
      newWidgets.delete(widgetType);
      newByCategory.get(widget.category)?.delete(widgetType);
      
      // Remove instances of this widget type
      for (const [id, instance] of newInstances) {
        if (instance.type === widgetType) {
          newInstances.delete(id);
        }
      }
      
      return { widgets: newWidgets, widgetsByCategory: newByCategory, instances: newInstances };
    });
  },

  unregisterPluginWidgets: (pluginId: string) => {
    set((state) => {
      const newWidgets = new Map(state.widgets);
      const newByCategory = new Map(state.widgetsByCategory);
      const newInstances = new Map(state.instances);
      
      for (const [type, widget] of newWidgets) {
        if (widget.pluginId === pluginId) {
          newWidgets.delete(type);
          newByCategory.get(widget.category)?.delete(type);
          
          // Remove instances
          for (const [id, instance] of newInstances) {
            if (instance.type === type) {
              newInstances.delete(id);
            }
          }
        }
      }
      
      return { widgets: newWidgets, widgetsByCategory: newByCategory, instances: newInstances };
    });
  },

  addInstance: (instance: WidgetInstance) => {
    set((state) => {
      const newInstances = new Map(state.instances);
      newInstances.set(instance.id, instance);
      return { instances: newInstances };
    });
  },

  updateInstance: (instanceId: string, updates: Partial<WidgetInstance>) => {
    set((state) => {
      const instance = state.instances.get(instanceId);
      if (!instance) return state;
      
      const newInstances = new Map(state.instances);
      newInstances.set(instanceId, { ...instance, ...updates });
      return { instances: newInstances };
    });
  },

  removeInstance: (instanceId: string) => {
    set((state) => {
      const newInstances = new Map(state.instances);
      newInstances.delete(instanceId);
      return { instances: newInstances };
    });
  },

  setEditingInstance: (instanceId: string | null) => {
    set({ editingInstanceId: instanceId });
  },

  getWidget: (widgetType: string) => {
    return get().widgets.get(widgetType);
  },

  getAllWidgets: () => {
    return Array.from(get().widgets.values()).filter(w => w.enabled);
  },

  getWidgetsByCategory: () => {
    const state = get();
    const result = new Map<string, WidgetDefinition[]>();
    
    for (const [category, widgetTypes] of state.widgetsByCategory) {
      const widgets = Array.from(widgetTypes)
        .map(type => state.widgets.get(type))
        .filter((w): w is WidgetDefinition => w !== undefined && w.enabled);
      
      if (widgets.length > 0) {
        result.set(category, widgets);
      }
    }
    
    return result;
  },

  getInstance: (instanceId: string) => {
    return get().instances.get(instanceId);
  },

  getAllInstances: () => {
    return Array.from(get().instances.values());
  },

  syncFromBackend: (widgets: WidgetDefinition[], instances: WidgetInstance[]) => {
    set(() => {
      const newWidgets = new Map<string, WidgetDefinition>();
      const newByCategory = new Map<string, Set<string>>();
      const newInstances = new Map<string, WidgetInstance>();
      
      for (const widget of widgets) {
        newWidgets.set(widget.type, widget);
        
        if (!newByCategory.has(widget.category)) {
          newByCategory.set(widget.category, new Set());
        }
        newByCategory.get(widget.category)!.add(widget.type);
      }
      
      for (const instance of instances) {
        newInstances.set(instance.id, instance);
      }
      
      return { widgets: newWidgets, widgetsByCategory: newByCategory, instances: newInstances };
    });
  },
}));

/**
 * Hook to get available widgets for adding to dashboard
 */
export function useAvailableWidgets() {
  return useWidgetRegistry((state) => state.getAllWidgets());
}

/**
 * Hook to get widget instances for a dashboard
 */
export function useDashboardWidgets() {
  return useWidgetRegistry((state) => state.getAllInstances());
}
